/**
 * Cohort transfer modal — MFix12.5b (2026-07-27).
 *
 * Two-column modal opened from the Army Info panel "Transfer Cohort"
 * button. Left column: source army roster (clickable cohorts). Right
 * column: target army selector (radio list of own-faction armies in
 * range) + read-only target roster. Bottom: instruction hint.
 *
 * Range: Euclidean ≤ `zoc_radius / 2` from source army position
 * (matches sim-side `AC_ZOC_RADIUS_FOR_TRANSFER`). Non-garrison cohorts
 * only (garrison cohorts filtered out of the source list — enforced
 * belt-and-suspenders sim-side too).
 *
 * Click a source cohort → dispatches `transfer_cohort` immediately
 * (source army, currently selected target, cohortId). No confirm step;
 * sim silently rejects if any validation fails so a stale click after
 * a battle-lock or range-drift is a no-op.
 *
 * Rebuild discipline: DOM rebuilt when source army changes OR when
 * the eligible-target set (by id) changes. Row text mutated per frame
 * (H/W/K counts + range distance) without rebuild.
 */

import { injectMenuStyles } from './menu-styles';
import { loadCohorts } from '../../data/cohorts';
import { loadDials, getDialNumber } from '../../data/dials';
import type { Command } from '../../sim/commands';
import type {
  ArmyId,
  Cohort,
  FactionId,
  GameState,
} from '../../sim/state';

const COHORTS = loadCohorts();
const TRANSFER_DIALS = loadDials();
const TRANSFER_RANGE = getDialNumber(TRANSFER_DIALS, 'zoc_radius') / 2;

export interface CohortTransferModalCallbacks {
  onIssue(cmd: Command): void;
  onClose(): void;
}

export interface CohortTransferModalHandle {
  sync(state: GameState, sourceArmyId: ArmyId | null, playerFactionId: FactionId | null): void;
  destroy(): void;
}

interface SourceRow {
  container: HTMLElement;
  button: HTMLButtonElement;
  label: HTMLElement;
  cohortId: number;
}

interface TargetRow {
  container: HTMLElement;
  radio: HTMLInputElement;
  label: HTMLElement;
  armyId: ArmyId;
}

export function mountCohortTransferModal(
  root: HTMLElement,
  callbacks: CohortTransferModalCallbacks,
): CohortTransferModalHandle {
  injectMenuStyles();

  const backdrop = document.createElement('div');
  backdrop.className = 'su-city-panel-backdrop';
  backdrop.style.display = 'none';
  backdrop.addEventListener('click', (e) => {
    if (e.target === backdrop) callbacks.onClose();
  });

  const panel = document.createElement('div');
  panel.className = 'su-city-panel';
  panel.style.maxWidth = '640px';
  backdrop.appendChild(panel);

  // Header ---------------------------------------------------------
  const header = document.createElement('div');
  header.className = 'su-city-panel-header';
  const title = document.createElement('h2');
  title.className = 'su-city-panel-title';
  title.textContent = 'Transfer Cohort';
  header.appendChild(title);
  const closeBtn = document.createElement('button');
  closeBtn.className = 'su-city-panel-close';
  closeBtn.textContent = '×';
  closeBtn.title = 'Close (Esc)';
  closeBtn.addEventListener('click', () => callbacks.onClose());
  header.appendChild(closeBtn);
  panel.appendChild(header);

  const body = document.createElement('div');
  body.className = 'su-city-panel-body';
  body.style.display = 'flex';
  body.style.gap = '12px';
  panel.appendChild(body);

  // Left column — source army cohorts
  const leftCol = document.createElement('div');
  leftCol.style.flex = '1';
  leftCol.style.minWidth = '0';
  const leftHeader = document.createElement('div');
  leftHeader.style.fontWeight = 'bold';
  leftHeader.style.marginBottom = '6px';
  leftHeader.textContent = 'Source (click to transfer)';
  leftCol.appendChild(leftHeader);
  const sourceList = document.createElement('div');
  leftCol.appendChild(sourceList);
  body.appendChild(leftCol);

  // Right column — target army picker + preview
  const rightCol = document.createElement('div');
  rightCol.style.flex = '1';
  rightCol.style.minWidth = '0';
  const rightHeader = document.createElement('div');
  rightHeader.style.fontWeight = 'bold';
  rightHeader.style.marginBottom = '6px';
  rightHeader.textContent = 'Target';
  rightCol.appendChild(rightHeader);
  const targetPickerLabel = document.createElement('div');
  targetPickerLabel.style.fontSize = '11px';
  targetPickerLabel.style.opacity = '0.7';
  targetPickerLabel.style.marginBottom = '4px';
  targetPickerLabel.textContent = 'Select target army:';
  rightCol.appendChild(targetPickerLabel);
  const targetList = document.createElement('div');
  targetList.style.marginBottom = '10px';
  rightCol.appendChild(targetList);
  const targetRosterLabel = document.createElement('div');
  targetRosterLabel.style.fontSize = '11px';
  targetRosterLabel.style.opacity = '0.7';
  targetRosterLabel.style.marginBottom = '4px';
  targetRosterLabel.textContent = 'Target roster:';
  rightCol.appendChild(targetRosterLabel);
  const targetRoster = document.createElement('div');
  targetRoster.style.fontSize = '11px';
  targetRoster.style.opacity = '0.85';
  targetRoster.style.paddingLeft = '10px';
  rightCol.appendChild(targetRoster);
  body.appendChild(rightCol);

  // Footer hint
  const hint = document.createElement('div');
  hint.style.marginTop = '10px';
  hint.style.padding = '6px 8px';
  hint.style.background = 'rgba(255,220,132,0.08)';
  hint.style.border = '1px solid rgba(255,220,132,0.35)';
  hint.style.borderRadius = '3px';
  hint.style.fontSize = '11px';
  hint.style.opacity = '0.85';
  hint.textContent = `Armies must be within ${TRANSFER_RANGE.toFixed(1)} cells. Garrison cohorts cannot be transferred.`;
  panel.appendChild(hint);

  root.appendChild(backdrop);

  // Local per-modal state
  let renderedSourceId: ArmyId | null = null;
  let renderedTargetSetKey = ''; // stringified sorted eligible-target ids
  let selectedTargetId: ArmyId | null = null;
  let sourceRows: SourceRow[] = [];
  let targetRows: TargetRow[] = [];
  let currentPlayerFactionId: FactionId | null = null;
  let lastKnownSourceId: ArmyId | null = null;

  function findEligibleTargets(state: GameState, sourceArmyId: ArmyId, playerFactionId: FactionId): ArmyId[] {
    const source = state.armies.get(sourceArmyId);
    if (!source) return [];
    if (source.currentBattleId !== null) return [];
    const eligible: ArmyId[] = [];
    for (const [aid, a] of state.armies) {
      if (aid === sourceArmyId) continue;
      if (a.ownerFactionId !== playerFactionId) continue;
      if (a.currentBattleId !== null) continue;
      const dx = source.position.x - a.position.x;
      const dy = source.position.y - a.position.y;
      if (Math.hypot(dx, dy) > TRANSFER_RANGE) continue;
      eligible.push(aid);
    }
    // Deterministic order by id.
    eligible.sort((a, b) => (a as number) - (b as number));
    return eligible;
  }

  function isTransferableCohort(cohort: Cohort): boolean {
    if (cohort.settlementId !== null) return false;
    if (cohort.worldStructureId !== null && cohort.worldStructureId !== undefined) return false;
    return true;
  }

  function labelForCohort(cohort: Cohort): string {
    const spec = COHORTS[cohort.type];
    const cap = spec?.figureCount ?? 60;
    const alive = cohort.aliveFigureCount;
    return `${cohort.type} (${alive}/${cap})`;
  }

  function buildSourceRows(sourceArmyId: ArmyId, state: GameState): void {
    sourceList.innerHTML = '';
    sourceRows = [];
    const source = state.armies.get(sourceArmyId);
    if (!source) return;
    for (const cid of source.cohortIds) {
      const cohort = state.cohorts.get(cid);
      if (!cohort) continue;
      if (!isTransferableCohort(cohort)) continue;
      const row = document.createElement('div');
      row.style.display = 'flex';
      row.style.alignItems = 'center';
      row.style.margin = '4px 0';
      const button = document.createElement('button');
      button.textContent = '→';
      button.title = 'Transfer to selected target';
      button.style.width = '28px';
      button.style.height = '24px';
      button.style.marginRight = '8px';
      button.style.background = '#4a3020';
      button.style.color = '#f5eacf';
      button.style.border = '1px solid #6a5238';
      button.style.borderRadius = '3px';
      button.style.cursor = 'pointer';
      button.style.fontFamily = 'inherit';
      button.style.fontSize = '12px';
      button.style.flexShrink = '0';
      button.addEventListener('click', () => {
        if (currentPlayerFactionId === null || lastKnownSourceId === null) return;
        if (selectedTargetId === null) return;
        callbacks.onIssue({
          type: 'transfer_cohort',
          tick: 0,
          playerFactionId: currentPlayerFactionId,
          fromArmyId: lastKnownSourceId,
          toArmyId: selectedTargetId,
          cohortId: cid,
        });
      });
      row.appendChild(button);
      const label = document.createElement('div');
      label.style.fontSize = '11px';
      label.textContent = labelForCohort(cohort);
      row.appendChild(label);
      sourceList.appendChild(row);
      sourceRows.push({ container: row, button, label, cohortId: cid as number });
    }
    if (sourceRows.length === 0) {
      const empty = document.createElement('div');
      empty.style.fontSize = '11px';
      empty.style.opacity = '0.6';
      empty.style.fontStyle = 'italic';
      empty.textContent = '(no transferable cohorts)';
      sourceList.appendChild(empty);
    }
  }

  function buildTargetRows(eligibleIds: readonly ArmyId[], state: GameState): void {
    targetList.innerHTML = '';
    targetRows = [];
    if (eligibleIds.length === 0) {
      const empty = document.createElement('div');
      empty.style.fontSize = '11px';
      empty.style.opacity = '0.6';
      empty.style.fontStyle = 'italic';
      empty.textContent = '(no armies in range)';
      targetList.appendChild(empty);
      selectedTargetId = null;
      return;
    }
    // Auto-select first if nothing valid selected.
    if (selectedTargetId === null || !eligibleIds.includes(selectedTargetId)) {
      selectedTargetId = eligibleIds[0]!;
    }
    for (const aid of eligibleIds) {
      const army = state.armies.get(aid);
      if (!army) continue;
      const row = document.createElement('label');
      row.style.display = 'flex';
      row.style.alignItems = 'center';
      row.style.margin = '3px 0';
      row.style.cursor = 'pointer';
      row.style.fontSize = '11px';
      const radio = document.createElement('input');
      radio.type = 'radio';
      radio.name = 'transfer-target';
      radio.value = String(aid);
      radio.checked = aid === selectedTargetId;
      radio.style.marginRight = '6px';
      radio.addEventListener('change', () => {
        if (radio.checked) selectedTargetId = aid;
      });
      row.appendChild(radio);
      const label = document.createElement('span');
      label.textContent = `Army #${aid} — ${army.cohortIds.length} cohorts`;
      row.appendChild(label);
      targetList.appendChild(row);
      targetRows.push({ container: row, radio, label, armyId: aid });
    }
  }

  function refreshLabels(state: GameState): void {
    // Update source-row labels (H/W/K may drift between recovery ticks).
    for (const row of sourceRows) {
      const c = state.cohorts.get(row.cohortId as import('../../sim/state').CohortId);
      if (!c) continue;
      row.label.textContent = labelForCohort(c);
    }
    // Update target-row labels + roster preview.
    for (const row of targetRows) {
      const a = state.armies.get(row.armyId);
      if (!a) continue;
      row.label.textContent = `Army #${row.armyId} — ${a.cohortIds.length} cohorts`;
    }
    targetRoster.innerHTML = '';
    if (selectedTargetId !== null) {
      const target = state.armies.get(selectedTargetId);
      if (target) {
        for (const cid of target.cohortIds) {
          const c = state.cohorts.get(cid);
          if (!c) continue;
          const row = document.createElement('div');
          row.textContent = `• ${labelForCohort(c)}`;
          targetRoster.appendChild(row);
        }
        if (target.cohortIds.length === 0) {
          const empty = document.createElement('div');
          empty.style.fontStyle = 'italic';
          empty.style.opacity = '0.6';
          empty.textContent = '(empty)';
          targetRoster.appendChild(empty);
        }
      }
    }
  }

  function sync(state: GameState, sourceArmyId: ArmyId | null, playerFactionId: FactionId | null): void {
    if (sourceArmyId === null || playerFactionId === null) {
      backdrop.style.display = 'none';
      renderedSourceId = null;
      renderedTargetSetKey = '';
      selectedTargetId = null;
      lastKnownSourceId = null;
      currentPlayerFactionId = null;
      return;
    }
    currentPlayerFactionId = playerFactionId;
    lastKnownSourceId = sourceArmyId;
    backdrop.style.display = 'flex';
    // Compute eligible targets each frame; rebuild when set changes.
    const eligible = findEligibleTargets(state, sourceArmyId, playerFactionId);
    const targetKey = eligible.map((a) => String(a)).join(',');
    if (sourceArmyId !== renderedSourceId) {
      buildSourceRows(sourceArmyId, state);
      renderedSourceId = sourceArmyId;
      selectedTargetId = null; // Reset target selection on source change.
      renderedTargetSetKey = '';
    }
    if (targetKey !== renderedTargetSetKey) {
      buildTargetRows(eligible, state);
      renderedTargetSetKey = targetKey;
    }
    // Header shows source army id + distance context.
    const source = state.armies.get(sourceArmyId);
    if (source) {
      title.textContent = `Transfer Cohort — Army #${sourceArmyId}`;
    }
    // Disable transfer buttons when there's no target.
    const hasTarget = selectedTargetId !== null;
    for (const row of sourceRows) row.button.disabled = !hasTarget;
    refreshLabels(state);
  }

  function destroy(): void {
    backdrop.remove();
  }

  return { sync, destroy };
}
